import { Dispatch } from 'redux'

import { useThunkActions } from 'redux/actions/useThunkActions'
import { create, destroy, getAll, update } from 'redux/api/ReportEditions'

import * as actions from './actions'

import { TAppliedFilter } from 'types/GlobalReport'
import { EPublicationStatus } from 'types/Enums'
import { toggleSnackbarOpen } from '../Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'

type NavigateCallbackFunction = (id: string) => void

export function tryGetAllReportEditions() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getAll())

    getAll()
      .then((response) => {
        dispatch(actions.getAllSuccess(response.data.data))
      })
      .catch(() => {
        dispatch(actions.getAllFailed())
      })
  }
}

function tryCreateReportEdition(
  body: { report_id: string; title?: string },
  navigate: NavigateCallbackFunction
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    create(body)
      .then((response) => {
        dispatch(actions.createSuccess(response.data.data))
        navigate(response.data.data.id)
      })
      .catch(() => {
        dispatch(actions.createFailed())
      })
  }
}

function tryDeleteReportEdition(id: string, navigate: () => void) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        dispatch(actions.destroySuccess(id))
        navigate()
      })
      .catch(() => {
        dispatch(actions.destroyFailed())
      })
  }
}

function tryUpdateReportEdition(
  id: string,
  body: {
    title?: string
    status?: string
    lock?: boolean
    from_date?: string | null
    to_date?: string | null
    applied_filters?: TAppliedFilter[]
  },
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, body)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))
        callback?.()
      })
      .catch(() => {
        dispatch(actions.updateFailed())
        callback?.(true)
      })
  }
}

function tryToggleReportEditionStatus(id: string, currentStatus: string) {
  return (dispatch: Dispatch) => {
    const isPublished = currentStatus === EPublicationStatus.PUBLISHED
    const nextStatus = isPublished
      ? EPublicationStatus.DRAFT
      : EPublicationStatus.PUBLISHED

    dispatch(actions.update())

    update(id, { status: nextStatus })
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))
        dispatch(
          toggleSnackbarOpen(
            isPublished
              ? SnackbarMessage.ReportEditionUnpublished
              : SnackbarMessage.ReportEditionPublished
          )
        )
      })
      .catch(() => {
        dispatch(actions.updateFailed())
        dispatch(
          toggleSnackbarOpen(
            isPublished
              ? SnackbarMessage.ReportEditionUnpublishedFailed
              : SnackbarMessage.ReportEditionPublishedFailed
          )
        )
      })
  }
}

export const useReportEditionActions = () => {
  return {
    tryCreateReportEdition: useThunkActions(tryCreateReportEdition),
    tryDeleteReportEdition: useThunkActions(tryDeleteReportEdition),
    tryGetAllReportEditions: useThunkActions(tryGetAllReportEditions),
    tryToggleReportEditionStatus: useThunkActions(tryToggleReportEditionStatus),
    tryUpdateReportEdition: useThunkActions(tryUpdateReportEdition)
  }
}
